import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { AuthenticationService, LoginPopoverService } from '@core/services';

import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ActionConfig } from '@core/models/action-config.model';
import { MenubarDesignTokens } from '@primeng/themes/types/menubar';
import { ProcessNotifierComponent } from '@ui/components/process-notifier.component';
import { MenuItem } from 'primeng/api';
import { Menubar } from 'primeng/menubar';
import { Popover } from 'primeng/popover';
import { Subscription } from 'rxjs';
import { LoginComponent } from 'src/apps/front-office/src/app/features/private/login/login.component';
import { UserInfoComponent } from './user-info.component';

@Component({
  selector: 'fl-fo-header',
  imports: [Menubar, Popover, LoginComponent, ProcessNotifierComponent, UserInfoComponent],
  template: `
    <header class="bg-white shadow-md py-4 px-6 md:px-12 flex items-center justify-between">
      <div class="flex items-center">
        <img src="./assets/images/logo-header.png" alt="Fare Lazio Logo" class="mr-4" />
      </div>
    </header>
    <p-menubar #menu [model]="items()" [dt]="MENU_THEME" class="font-semibold">
      <ng-template #end>
        @if (isAuthenticated()) {
          <div class="flex items-center gap-4">
            <fl-process-notifier />
            <fl-fo-user-info [actions]="userActions" (action)="onUserAction($event)" (loggedOut)="onLoggedOut()" />
          </div>
        } @else {
          <div class="p-menubar-item">
            <div class="p-menubar-item-content">
              <a
                #areaRiservataLink
                pRipple
                class="p-menubar-item-link text-white hover:text-primary-500 font-semibold"
                tabindex="0"
                (click)="op.toggle($event)"
                (keyup.enter)="op.toggle($event)">
                <em class="pi pi-lock p-menubar-item-icon"></em>
                <span class="p-menubar-item-label hidden xl:block">AREA RISERVATA</span>
              </a>
              <p-popover #op [appendTo]="'body'">
                <fl-fo-login (login)="hidePopover()" (register)="hidePopover()"></fl-fo-login>
              </p-popover>
            </div>
          </div>
        }
      </ng-template>
    </p-menubar>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private authService = inject(AuthenticationService);
  private router = inject(Router);
  private loginPopoverService = inject(LoginPopoverService);
  private subscription: Subscription | undefined;

  @ViewChild('op') op: Popover | undefined;
  @ViewChild('areaRiservataLink', { read: ElementRef }) areaRiservataLink: ElementRef | undefined;

  readonly MENU_THEME: MenubarDesignTokens = {
    root: {
      background: 'var(--p-primary-500)',
      borderRadius: 'none',
      color: 'var(--color-white)',
    },
    item: {
      color: 'var(--color-white)',
      activeColor: 'var(--p-primary-500)',
    },
    submenu: {
      background: 'var(--p-primary-500)',
    },
  };

  isAuthenticated = toSignal(this.authService.isAuthenticated$, {
    initialValue: false,
  });

  userActions: ActionConfig[] = [
    { key: 'dashboard', label: 'Area riservata', icon: 'pi pi-lock' },
    { key: 'profilo', label: 'Profilo', icon: 'pi pi-user' },
  ];

  items = signal([
    {
      label: 'HOME',
      routerLink: ['/home'],
    },
    {
      label: 'NEWS',
      routerLink: ['/news'],
    },
    {
      label: 'CHI SIAMO',
      routerLink: ['/about-us'],
    },
    {
      label: 'BANDI GESTITI',
      items: [
        {
          label: 'BANDI APERTI / PROSSIMA APERTURA',
          routerLink: ['/bandi', 'aperti'],
        },
        { label: 'BANDI CHIUSI', routerLink: ['/bandi', 'chiusi'] },
      ],
    },
    {
      label: 'CONTATTI',
      routerLink: ['/contatti'],
    },
    {
      label: 'PRIVACY & TRASPARENZA',
      routerLink: ['/privacy-trasparenza'],
    },
    {
      label: 'PUNTI DI CONTATTO',
      routerLink: ['/punti-di-contatto'],
    },
  ] as MenuItem[]);

  hidePopover() {
    if (this.op) this.op.hide();
  }

  ngOnInit() {
    // Sottoscrizione al servizio per aprire il popover
    this.subscription = this.loginPopoverService.openPopover$.subscribe(() => {
      this.openLoginPopover();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  /**
   * Apre il popover di login e mette il focus
   */
  async openLoginPopover() {
    if (!this.op || !this.areaRiservataLink) return;

    // Scrolla in alto
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Aspetta che lo scroll sia completato
    await this.waitForScrollEnd();

    // Apre il popover
    const event = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    this.op.toggle(event, this.areaRiservataLink.nativeElement);

    // Aspetta che l'input sia effettivamente nel DOM
    const firstInput = await this.waitForElement('fl-fo-login input[type="text"], fl-fo-login input[type="email"]');

    // Mette il focus
    if (firstInput) {
      firstInput.focus();
    }
  }

  /**
   * Aspetta che lo scroll sia completato
   */
  private waitForScrollEnd(): Promise<void> {
    return new Promise(resolve => {
      const checkScroll = () => {
        if (window.scrollY === 0) {
          resolve();
        } else {
          requestAnimationFrame(checkScroll);
        }
      };
      // Inizia a controllare dopo un frame per dare tempo allo scroll di partire
      requestAnimationFrame(checkScroll);
    });
  }

  /**
   * Aspetta che un elemento appaia nel DOM
   */
  private waitForElement(selector: string): Promise<HTMLElement | null> {
    return new Promise(resolve => {
      const element = document.querySelector(selector) as HTMLElement;
      if (element) {
        // Elemento già presente, aspetta un frame per sicurezza
        requestAnimationFrame(() => resolve(element));
        return;
      }

      // Usa MutationObserver per aspettare che l'elemento appaia
      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector) as HTMLElement;
        if (element) {
          observer.disconnect();
          requestAnimationFrame(() => resolve(element));
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Timeout di sicurezza di 2 secondi
      setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, 2000);
    });
  }

  onUserAction(key: string) {
    switch (key) {
      case 'dashboard':
        // rotte area privata
        this.navigate(['/private/dashboard']);
        break;
      case 'profilo':
        this.navigate(['/private/profilo']);
        break;
    }
  }

  onLoggedOut() {
    this.navigate(['/home']);
  }

  private navigate(commands: string[]) {
    this.router.navigate(commands);
  }
}
